import { useEffect, useState, useMemo } from 'react';
import { Plus, Edit, Power, Zap, Search, Dumbbell, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { supabase } from '../services/supabase';
import TitularCuenta from '../components/TitularCuenta';
import Modal from '../components/ui/Modal';
import Confirm from '../components/ui/Confirm';
import type { Database } from '../types/database.types';
import type { CreditType, PackType } from '../types/database';

type PackRow = Database['public']['Tables']['session_packs']['Row'];
type UserCreditRow = Database['public']['Tables']['user_credits']['Row'];

// Pack: subset que la página usa, con uniones literales recuperadas del
// `database.ts` manual — el código filtra packs por `credit_type` y por
// `type` (ver `packsGrupal` / `packsIndividual` / `packsEspecial` / `packsGym`
// más abajo), así que merece el narrowing.
type Pack = Omit<
  Pick<
    PackRow,
    | 'id'
    | 'name'
    | 'type'
    | 'credit_type'
    | 'sessions_count'
    | 'price_cents'
    | 'validity_days'
    | 'description'
    | 'active'
  >,
  'type' | 'credit_type'
> & { type: PackType; credit_type: CreditType };

// Credit: shape post-procesado (los embeds `profiles` / `session_packs` se
// renombran a `profile` / `pack` en `loadData`, por compat con la UI).
interface Credit {
  id: string;
  // Nullable desde la mig 207: el bono sobrevive al borrado de su titular
  // (obligación contable) y el nombre pasa a `billing_name`.
  user_id: string | null;
  billing_name: string | null;
  sessions_total: number;
  sessions_used: number;
  sessions_remaining: number;
  purchase_date: string;
  expiry_date: string | null;
  active: boolean;
  credit_type: CreditType;
  profile?: { full_name: string; email: string | null } | null;
  pack?: { name: string } | null;
}

// Forma cruda que devuelve PostgREST para el `.select('*, profiles(...), session_packs(...)')`.
// Los embeds no se infieren del Row generado: hay que componerlos.
type CreditRowWithRelations = UserCreditRow & {
  profiles: { full_name: string; email: string | null } | null;
  session_packs: { name: string } | null;
};
interface CreditGroup {
  user_id: string | null;
  billing_name: string | null;
  credit_type: CreditType;
  profile: { full_name: string; email: string | null } | null;
  sessions_total: number;
  sessions_used: number;
  sessions_remaining: number;
  hasActive: boolean;
  bonos: Credit[];
}
interface PackForm {
  name: string;
  type: string;
  credit_type: string;
  sessions_count: number;
  price_cents: number;
  validity_days: number;
  description: string;
}

const FORM_EMPTY: PackForm = {
  name: '',
  type: 'bono',
  credit_type: 'grupal',
  sessions_count: 5,
  price_cents: 8500,
  validity_days: 180,
  description: '',
};

const GYM_FORM_EMPTY: PackForm = {
  name: '',
  type: 'bono',
  credit_type: 'gym',
  sessions_count: 1,
  price_cents: 10000,
  validity_days: 30,
  description: '',
};

const CREDIT_TYPE_LABELS: Record<string, string> = {
  grupal: 'Grupal',
  individual: 'Individual',
  especial: 'Especial',
  gym: 'PF',
};
const CREDIT_TYPE_COLORS: Record<string, string> = {
  grupal: 'badge-gold',
  individual: 'badge-silver',
  especial: 'badge-warning',
  gym: 'badge-success',
};

export default function BonosPage() {
  const [packs, setPacks] = useState<Pack[]>([]);
  const [credits, setCredits] = useState<Credit[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'packs' | 'suscripciones' | 'creditos'>('packs');
  const [modal, setModal] = useState(false);
  const [gymModal, setGymModal] = useState(false);
  const [editTarget, setEditTarget] = useState<Pack | null>(null);
  const [form, setForm] = useState<PackForm>(FORM_EMPTY);
  const [gymForm, setGymForm] = useState<PackForm>(GYM_FORM_EMPTY);
  const [saving, setSaving] = useState(false);
  const [confirmToggle, setConfirmToggle] = useState<Pack | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Pack | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // El interruptor de un pack es una acción de fila y no tiene modal donde
  // contar un rechazo: lo pintaba en pantalla igualmente.
  const [listError, setListError] = useState('');
  // El ajuste de créditos SÍ tiene modal, y ahí es donde debe verse: si falla,
  // el modal se queda abierto en vez de cerrarse como si hubiera cuadrado.
  const [ajusteError, setAjusteError] = useState('');
  // Los dos modales de pack (grupal y gimnasio) cerraban y recargaban sin
  // mirar si el guardado había entrado: el admin veía desaparecer el modal y
  // daba por hecho que el pack estaba creado.
  const [modalError, setModalError] = useState('');
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState<string>('todos');
  const [ajusteModal, setAjusteModal] = useState<Credit | null>(null);
  const [ajusteVal, setAjusteVal] = useState(0);
  const [ajusteMotivo, setAjusteMotivo] = useState('');
  const [detalleGroup, setDetalleGroup] = useState<CreditGroup | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    const [{ data: ps, error: packsError }, { data: cs, error: credsError }] = await Promise.all([
      supabase.from('session_packs').select('*').order('sessions_count'),
      supabase
        .from('user_credits')
        // [BUG 9] FK NOMBRADA. `user_credits` tiene dos claves ajenas a
        // `profiles` —`user_id` y `granted_by`—: sin desambiguar, PostgREST
        // responde 300 PGRST201 y la lista de bonos sale vacía entera.
        .select('*, profiles!user_id(full_name, email), session_packs(name)')
        .order('purchase_date', { ascending: false }),
    ]);
    // Un fallo de lectura se dice. Pintar cero bonos cuando lo que ha pasado es
    // que la consulta no entró es la manera de que nadie se entere.
    if (packsError || credsError) {
      setListError(`No se pudieron cargar los bonos: ${(packsError ?? credsError)?.message}`);
      setLoading(false);
      return;
    }
    setListError('');
    setPacks((ps ?? []) as Pack[]);
    const rows = (cs ?? []) as CreditRowWithRelations[];
    setCredits(
      rows.map((c) => ({
        id: c.id,
        user_id: c.user_id,
        billing_name: c.billing_name,
        sessions_total: c.sessions_total,
        sessions_used: c.sessions_used,
        sessions_remaining: c.sessions_remaining,
        purchase_date: c.purchase_date,
        expiry_date: c.expiry_date,
        active: c.active,
        credit_type: c.credit_type as CreditType,
        profile: c.profiles,
        pack: c.session_packs,
      }))
    );
    setLoading(false);
  }

  async function handleSave() {
    setSaving(true);
    setModalError('');
    const payload = { ...form, description: form.description || null };
    const { error } = editTarget
      ? await supabase.from('session_packs').update(payload).eq('id', editTarget.id)
      : await supabase.from('session_packs').insert(payload);
    if (error) {
      setModalError(`No se pudo guardar el pack: ${error.message}`);
      setSaving(false);
      return;
    }
    await loadData();
    setModal(false);
    setSaving(false);
  }

  async function handleSaveGym() {
    setSaving(true);
    const payload = {
      ...gymForm,
      credit_type: 'gym',
      sessions_count: 1,
      description: gymForm.description || null,
    };
    setModalError('');
    const { error } = editTarget
      ? await supabase.from('session_packs').update(payload).eq('id', editTarget.id)
      : await supabase.from('session_packs').insert(payload);
    if (error) {
      setModalError(`No se pudo guardar el pack de gimnasio: ${error.message}`);
      setSaving(false);
      return;
    }
    await loadData();
    setGymModal(false);
    setSaving(false);
  }

  async function handleDeletePack(p: Pack) {
    // ⚠️ ESTE RECUENTO ES UNA PROTECCIÓN, Y UN FALLO LA ABRÍA.
    //
    // `const { count } = await …` descartaba el error, y con `count` a null la
    // condición de abajo (`count && count > 0`) es FALSA: se seguía al
    // `.delete()`. O sea que no saber cuántos bonos de alumnos cuelgan del
    // pack se leía como «no cuelga ninguno», y se borraba.
    //
    // La dirección segura es la contraria: si no se puede contar, no se borra.
    const { count, error: errorConteo } = await supabase
      .from('user_credits')
      .select('id', { count: 'exact', head: true })
      .eq('pack_id', p.id);
    if (errorConteo) {
      setDeleteError(
        `No se puede eliminar "${p.name}": no se ha podido comprobar cuántos bonos de alumnos cuelgan de él. Inténtalo de nuevo; no se ha borrado nada.`
      );
      setConfirmDelete(null);
      return;
    }
    if (count && count > 0) {
      setDeleteError(
        `No se puede eliminar "${p.name}": tiene ${count} bono${count > 1 ? 's' : ''} de alumnos asociados. Desactívalo en su lugar.`
      );
      setConfirmDelete(null);
      return;
    }
    const { error } = await supabase.from('session_packs').delete().eq('id', p.id);
    if (error) {
      setDeleteError(`No se pudo eliminar "${p.name}": ${error.message}`);
      setConfirmDelete(null);
      return;
    }
    await loadData();
    setConfirmDelete(null);
  }

  async function togglePack(p: Pack) {
    const { error } = await supabase
      .from('session_packs')
      .update({ active: !p.active })
      .eq('id', p.id);
    // El error se comprueba ANTES de tocar el estado: pintar el pack como
    // desactivado cuando la base ha dicho que no es peor que no pintar nada,
    // porque el cambio falso sobrevive hasta la siguiente recarga.
    if (error) {
      setListError(
        `No se pudo ${p.active ? 'desactivar' : 'activar'} "${p.name}": ${error.message}`
      );
      return;
    }
    setListError('');
    setPacks((prev) => prev.map((x) => (x.id === p.id ? { ...x, active: !x.active } : x)));
  }

  async function ajustarCredito() {
    if (!ajusteModal) return;
    setAjusteError('');

    // ⚠️ ESTO ERA UN `.from('user_credits').update({...})` DIRECTO.
    //
    // Movía el saldo de un alumno —dinero— sin dejar recibo en ninguna parte,
    // esquivando el camino que sí lo deja (`admin_grant_credits_v1` y
    // `void_manual_bono_activation`, que escriben en `manual_bono_activations`
    // Y en `audit_log`). Dos formas de mover lo mismo, una con papel y otra sin.
    //
    // Desde la mig 271 `user_credits` no es escribible desde PostgREST: la
    // tabla se quedó sin políticas de escritura y con un trigger detrás. El
    // ajuste va por `admin_adjust_credit`, que exige motivo y audita.
    //
    // El motivo NO es decorativo: un ajuste de saldo sin explicación es
    // indistinguible de un error de dedo tres meses después.
    if (!ajusteMotivo.trim()) {
      setAjusteError('El motivo es obligatorio: queda registrado con el ajuste.');
      return;
    }

    const { data, error } = await supabase.rpc('admin_adjust_credit', {
      p_credit_id: ajusteModal.id,
      p_delta: ajusteVal,
      p_reason: ajusteMotivo.trim(),
    });

    // Esto cambia las sesiones que le quedan a un alumno. Cerrar el modal sin
    // mirar el resultado es decirle al admin que el ajuste cuadró.
    if (error) {
      setAjusteError(`No se pudo aplicar el ajuste: ${error.message}`);
      return;
    }
    // La RPC devuelve `{ok:false, error}` para lo que no es un fallo de base
    // (delta cero, motivo vacío, bono inexistente). Sin esto, el panel diría
    // que el ajuste se aplicó cuando la base ha dicho que no.
    const res = data as { ok?: boolean; error?: string } | null;
    if (!res?.ok) {
      setAjusteError(`No se pudo aplicar el ajuste: ${res?.error ?? 'respuesta inesperada'}`);
      return;
    }

    await loadData();
    setAjusteModal(null);
    setAjusteVal(0);
    setAjusteMotivo('');
  }

  const packsGrupal = packs.filter((p) => p.credit_type === 'grupal');
  const packsIndividual = packs.filter((p) => p.credit_type === 'individual');
  const packsEspecial = packs.filter((p) => p.credit_type === 'especial');
  const packsGym = packs.filter((p) => p.credit_type === 'gym');

  const creditosFiltrados = credits.filter((c) => {
    const matchSearch =
      (c.profile?.full_name ?? c.billing_name ?? '').toLowerCase().includes(search.toLowerCase()) ||
      (c.profile?.email ?? '').toLowerCase().includes(search.toLowerCase());
    const matchType = filterType === 'todos' || c.credit_type === filterType;
    return matchSearch && matchType;
  });

  const creditosAgrupados = useMemo<CreditGroup[]>(() => {
    const map = new Map<string, CreditGroup>();
    creditosFiltrados.forEach((c) => {
      // ⚠️ Sin `user_id` la clave NO puede ser `null`: dos personas borradas
      // distintas caerían en el mismo grupo y sus bonos se sumarían juntos. Se
      // agrupa por el nombre sellado, y a falta de él por el id del bono.
      const key = c.user_id
        ? `${c.user_id}::${c.credit_type}`
        : `del:${c.billing_name ?? c.id}::${c.credit_type}`;
      if (!map.has(key)) {
        map.set(key, {
          user_id: c.user_id,
          billing_name: c.billing_name,
          credit_type: c.credit_type,
          profile: c.profile ?? null,
          sessions_total: 0,
          sessions_used: 0,
          sessions_remaining: 0,
          hasActive: false,
          bonos: [],
        });
      }
      const g = map.get(key)!;
      g.sessions_total += c.sessions_total;
      g.sessions_used += c.sessions_used;
      g.sessions_remaining += c.sessions_remaining;
      g.hasActive = g.hasActive || c.active;
      g.bonos.push(c);
    });
    return Array.from(map.values()).sort((a, b) =>
      (a.profile?.full_name ?? a.billing_name ?? '').localeCompare(
        b.profile?.full_name ?? b.billing_name ?? ''
      )
    );
  }, [creditosFiltrados]);

  function PackCard({
    p,
    onEdit,
    onToggle,
    onDelete,
  }: {
    p: Pack;
    onEdit: () => void;
    onToggle: () => void;
    onDelete: () => void;
  }) {
    const isGym = p.credit_type === 'gym';
    return (
      <div key={p.id} className={`card ${!p.active ? 'opacity-60' : ''}`}>
        <div className="flex justify-between mb-3">
          {isGym ? (
            <Dumbbell size={20} className="text-green-400" />
          ) : (
            <Zap size={20} className="text-gold-primary" />
          )}
          <div className="flex gap-2">
            <button onClick={onEdit} className="text-text-secondary hover:text-gold-primary">
              <Edit size={14} />
            </button>
            <button onClick={onToggle} className="text-text-secondary hover:text-text-secondary">
              <Power size={14} />
            </button>
            <button onClick={onDelete} className="text-text-secondary hover:text-error">
              <Trash2 size={14} />
            </button>
          </div>
        </div>
        <h3 className="font-oswald text-white uppercase tracking-wide mb-1">{p.name}</h3>
        <p className={`font-oswald text-3xl ${isGym ? 'text-green-400' : 'text-gold-primary'}`}>
          {(p.price_cents / 100).toFixed(2)}€
        </p>
        <p className="text-text-secondary text-sm">{p.validity_days} días</p>
        {p.description && <p className="text-text-secondary text-xs mt-2">{p.description}</p>}
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <span className={CREDIT_TYPE_COLORS[p.credit_type ?? 'grupal'] ?? 'badge-gold'}>
            {CREDIT_TYPE_LABELS[p.credit_type ?? 'grupal']}
          </span>
          <span className={p.active ? 'badge-success' : 'badge-error'}>
            {p.active ? 'Activo' : 'Inactivo'}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {listError && (
        <p
          className="text-error text-sm border border-error/30 rounded px-3 py-2"
          data-testid="bonos-list-error"
        >
          {listError}
        </p>
      )}

      <div className="flex gap-3">
        <button
          onClick={() => setTab('packs')}
          className={tab === 'packs' ? 'btn-gold' : 'btn-ghost'}
        >
          Packs de bonos
        </button>
        <button
          onClick={() => setTab('suscripciones')}
          className={tab === 'suscripciones' ? 'btn-gold' : 'btn-ghost'}
        >
          Suscripciones Preparación Física
        </button>
        <button
          onClick={() => setTab('creditos')}
          className={tab === 'creditos' ? 'btn-gold' : 'btn-ghost'}
        >
          Bonos por alumno
        </button>
      </div>

      {tab === 'packs' && (
        <>
          <div className="flex justify-end">
            <button
              onClick={() => {
                setEditTarget(null);
                setForm(FORM_EMPTY);
                setModal(true);
              }}
              className="btn-gold flex items-center gap-2"
            >
              <Plus size={16} /> Nuevo Pack
            </button>
          </div>
          {loading ? (
            <div className="flex justify-center h-48 items-center">
              <div className="w-8 h-8 border-2 border-gold-primary/30 border-t-gold-primary rounded-full animate-spin" />
            </div>
          ) : (
            <div className="space-y-8">
              {[
                { label: 'Clases Grupales', list: packsGrupal },
                { label: 'Clases Individuales', list: packsIndividual },
                { label: 'Clases Especiales', list: packsEspecial },
              ].map(({ label, list }) => (
                <div key={label}>
                  <h3 className="font-oswald text-text-secondary uppercase tracking-widest text-xs mb-3 flex items-center gap-2">
                    <span className="flex-1 border-b border-bg-border" />
                    {label}
                    <span className="flex-1 border-b border-bg-border" />
                  </h3>
                  {list.length === 0 ? (
                    <p className="text-text-secondary text-sm text-center py-4">
                      Sin packs en esta categoría
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                      {list.map((p) => (
                        <PackCard
                          key={p.id}
                          p={p}
                          onEdit={() => {
                            setEditTarget(p);
                            setForm({
                              name: p.name,
                              type: p.type,
                              credit_type: p.credit_type ?? 'grupal',
                              sessions_count: p.sessions_count,
                              price_cents: p.price_cents,
                              validity_days: p.validity_days,
                              description: p.description ?? '',
                            });
                            setModal(true);
                          }}
                          onToggle={() => setConfirmToggle(p)}
                          onDelete={() => setConfirmDelete(p)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {tab === 'suscripciones' && (
        <>
          <div className="flex justify-end">
            <button
              onClick={() => {
                setEditTarget(null);
                setGymForm(GYM_FORM_EMPTY);
                setGymModal(true);
              }}
              className="btn-gold flex items-center gap-2"
            >
              <Plus size={16} /> Nueva Suscripción Prep. Fís.
            </button>
          </div>
          <p className="text-text-secondary text-sm">
            Las suscripciones Preparación Física dan acceso al módulo de entrenamiento
            personalizado. Se recomiendan 3 planes: 1 mes, 3 meses y 12 meses.
          </p>
          {loading ? (
            <div className="flex justify-center h-48 items-center">
              <div className="w-8 h-8 border-2 border-gold-primary/30 border-t-gold-primary rounded-full animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              {packsGym.map((p) => (
                <PackCard
                  key={p.id}
                  p={p}
                  onEdit={() => {
                    setEditTarget(p);
                    setGymForm({
                      name: p.name,
                      type: p.type,
                      credit_type: 'gym',
                      sessions_count: 1,
                      price_cents: p.price_cents,
                      validity_days: p.validity_days,
                      description: p.description ?? '',
                    });
                    setGymModal(true);
                  }}
                  onToggle={() => setConfirmToggle(p)}
                  onDelete={() => setConfirmDelete(p)}
                />
              ))}
              {packsGym.length === 0 && (
                <div className="col-span-4 text-center py-16 text-text-secondary">
                  <Dumbbell size={40} className="mx-auto mb-4 opacity-30" />
                  <p>
                    No hay suscripciones Preparación Física. Crea las 3 recomendadas: 1 mes (100€),
                    3 meses (250€), 12 meses (800€).
                  </p>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {tab === 'creditos' && (
        <>
          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative w-72">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary"
              />
              <input
                className="input pl-9 h-9 text-sm"
                placeholder="Buscar alumno..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              {(['todos', 'grupal', 'individual', 'especial', 'gym'] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setFilterType(t)}
                  className={
                    filterType === t
                      ? 'btn-gold text-xs px-3 py-1.5'
                      : 'btn-ghost text-xs px-3 py-1.5'
                  }
                >
                  {t === 'todos' ? 'Todos' : CREDIT_TYPE_LABELS[t]}
                </button>
              ))}
            </div>
          </div>
          <div className="card p-0 overflow-hidden">
            <table className="w-full">
              <thead className="border-b border-bg-border bg-bg-elevated">
                <tr>
                  <th className="table-th">Alumno</th>
                  <th className="table-th">Tipo</th>
                  <th className="table-th">Total</th>
                  <th className="table-th">Usadas</th>
                  <th className="table-th">Restantes</th>
                  <th className="table-th">Estado</th>
                  <th className="table-th">Detalle</th>
                </tr>
              </thead>
              <tbody>
                {creditosAgrupados.map((g, i) => {
                  const isFirstOfUser = i === 0 || creditosAgrupados[i - 1].user_id !== g.user_id;
                  return (
                    <tr
                      key={`${g.user_id}-${g.credit_type}`}
                      className={`table-row ${isFirstOfUser && i > 0 ? 'border-t-2 border-bg-border' : ''}`}
                    >
                      <td className="table-td">
                        {isFirstOfUser ? (
                          <div>
                            <p className="font-medium">
                              <TitularCuenta
                                userId={g.user_id}
                                fullName={g.profile?.full_name}
                                billingName={g.billing_name}
                              />
                            </p>
                            <p className="text-text-secondary text-xs">{g.profile?.email}</p>
                          </div>
                        ) : null}
                      </td>
                      <td className="table-td">
                        <span
                          className={CREDIT_TYPE_COLORS[g.credit_type ?? 'grupal'] ?? 'badge-gold'}
                        >
                          {CREDIT_TYPE_LABELS[g.credit_type ?? 'grupal']}
                        </span>
                      </td>
                      <td className="table-td">{g.sessions_total}</td>
                      <td className="table-td">{g.sessions_used}</td>
                      <td className="table-td">
                        <span
                          className={`font-oswald text-lg ${g.sessions_remaining <= 2 ? 'text-error' : 'text-gold-primary'}`}
                        >
                          {g.sessions_remaining}
                        </span>
                      </td>
                      <td className="table-td">
                        <span className={g.hasActive ? 'badge-success' : 'badge-error'}>
                          {g.hasActive ? 'Activo' : 'Expirado'}
                        </span>
                      </td>
                      <td className="table-td">
                        <button
                          onClick={() => setDetalleGroup(g)}
                          className="btn-ghost text-xs px-2 py-1"
                        >
                          Ver detalle
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {creditosAgrupados.length === 0 && (
                  <tr>
                    <td colSpan={7} className="table-td text-center text-text-secondary py-12">
                      No se encontraron bonos
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Modal pack estándar */}
      <Modal
        open={modal}
        onClose={() => setModal(false)}
        title={editTarget ? 'Editar Pack' : 'Nuevo Pack'}
      >
        <div className="space-y-4">
          <div>
            <label className="label">Nombre *</label>
            <input
              className="input"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Tipo de bono</label>
              <select
                className="input"
                value={form.type}
                onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
              >
                <option value="individual">Individual</option>
                <option value="bono">Bono</option>
              </select>
            </div>
            <div>
              <label className="label">Tipo de sesión</label>
              <select
                className="input"
                value={form.credit_type}
                onChange={(e) => setForm((f) => ({ ...f, credit_type: e.target.value }))}
              >
                <option value="grupal">Grupal (sesiones grupales)</option>
                <option value="individual">Individual (sesiones 1-a-1)</option>
                <option value="especial">Especial (clases especiales)</option>
              </select>
            </div>
            <div>
              <label className="label">Nº sesiones</label>
              <input
                type="number"
                min={1}
                className="input"
                value={form.sessions_count}
                onChange={(e) => setForm((f) => ({ ...f, sessions_count: +e.target.value }))}
              />
            </div>
            <div>
              <label className="label">Precio (€)</label>
              <input
                type="number"
                step="0.01"
                className="input"
                value={(form.price_cents / 100).toFixed(2)}
                onChange={(e) =>
                  setForm((f) => ({ ...f, price_cents: Math.round(+e.target.value * 100) }))
                }
              />
            </div>
            <div className="col-span-2">
              <label className="label">Validez (días)</label>
              <input
                type="number"
                min={1}
                className="input"
                value={form.validity_days}
                onChange={(e) => setForm((f) => ({ ...f, validity_days: +e.target.value }))}
              />
            </div>
          </div>
          <div>
            <label className="label">Descripción</label>
            <textarea
              className="input resize-none"
              rows={2}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            />
          </div>
          {modalError && (
            <p className="text-error text-sm" data-testid="bonos-modal-error">
              {modalError}
            </p>
          )}
          <div className="flex justify-end gap-3">
            <button onClick={() => setModal(false)} className="btn-ghost">
              Cancelar
            </button>
            <button onClick={handleSave} disabled={saving} className="btn-gold">
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal suscripción GYM */}
      <Modal
        open={gymModal}
        onClose={() => setGymModal(false)}
        title={
          editTarget
            ? 'Editar Suscripción Preparación Física'
            : 'Nueva Suscripción Preparación Física'
        }
      >
        <div className="space-y-4">
          <p className="text-text-secondary text-xs">
            Tipo fijado: <span className="badge-success">PF</span> · Sesiones: 1 (suscripción de
            acceso, no de consumo)
          </p>
          <div>
            <label className="label">Nombre *</label>
            <input
              className="input"
              value={gymForm.name}
              placeholder="Ej: PF 1 mes"
              onChange={(e) => setGymForm((f) => ({ ...f, name: e.target.value }))}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Precio (€)</label>
              <input
                type="number"
                step="0.01"
                className="input"
                value={(gymForm.price_cents / 100).toFixed(2)}
                onChange={(e) =>
                  setGymForm((f) => ({ ...f, price_cents: Math.round(+e.target.value * 100) }))
                }
              />
            </div>
            <div>
              <label className="label">Validez (días)</label>
              <input
                type="number"
                min={1}
                className="input"
                value={gymForm.validity_days}
                onChange={(e) => setGymForm((f) => ({ ...f, validity_days: +e.target.value }))}
              />
            </div>
          </div>
          <div>
            <label className="label">Descripción</label>
            <textarea
              className="input resize-none"
              rows={2}
              value={gymForm.description}
              onChange={(e) => setGymForm((f) => ({ ...f, description: e.target.value }))}
            />
          </div>
          {modalError && (
            <p className="text-error text-sm" data-testid="bonos-gym-modal-error">
              {modalError}
            </p>
          )}
          <div className="flex justify-end gap-3">
            <button onClick={() => setGymModal(false)} className="btn-ghost">
              Cancelar
            </button>
            <button onClick={handleSaveGym} disabled={saving} className="btn-gold">
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal detalle de bonos agrupados */}
      <Modal
        open={!!detalleGroup}
        onClose={() => setDetalleGroup(null)}
        title={
          detalleGroup
            ? `Bonos ${CREDIT_TYPE_LABELS[detalleGroup.credit_type]} — ${detalleGroup.profile?.full_name}`
            : ''
        }
        size="xl"
      >
        {detalleGroup && (
          <div className="space-y-4">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-bg-border">
                  <tr>
                    <th className="table-th">Pack</th>
                    <th className="table-th">Total</th>
                    <th className="table-th">Usadas</th>
                    <th className="table-th">Restantes</th>
                    <th className="table-th">Caduca</th>
                    <th className="table-th">Estado</th>
                    <th className="table-th">Ajuste</th>
                  </tr>
                </thead>
                <tbody>
                  {detalleGroup.bonos.map((c) => (
                    <tr key={c.id} className="table-row">
                      <td className="table-td text-text-secondary">{c.pack?.name ?? '—'}</td>
                      <td className="table-td">{c.sessions_total}</td>
                      <td className="table-td">{c.sessions_used}</td>
                      <td className="table-td">
                        <span
                          className={`font-oswald text-lg ${c.sessions_remaining <= 2 ? 'text-error' : 'text-gold-primary'}`}
                        >
                          {c.sessions_remaining}
                        </span>
                      </td>
                      <td className="table-td text-text-secondary text-xs">
                        {c.expiry_date ? format(new Date(c.expiry_date), 'dd/MM/yyyy') : '—'}
                      </td>
                      <td className="table-td">
                        <span className={c.active ? 'badge-success' : 'badge-error'}>
                          {c.active ? 'Activo' : 'Expirado'}
                        </span>
                      </td>
                      <td className="table-td">
                        <button
                          onClick={() => {
                            setDetalleGroup(null);
                            setAjusteModal(c);
                            setAjusteVal(0);
                          }}
                          className="btn-ghost text-xs px-2 py-1"
                        >
                          Ajustar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="pt-2 border-t border-bg-border flex justify-between text-sm text-text-secondary">
              <span>
                Total bonos: <strong className="text-white">{detalleGroup.bonos.length}</strong>
              </span>
              <span>
                Sesiones restantes:{' '}
                <strong className="text-gold-primary font-oswald text-base">
                  {detalleGroup.sessions_remaining}
                </strong>
              </span>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal ajuste manual */}
      <Modal
        open={!!ajusteModal}
        onClose={() => setAjusteModal(null)}
        title="Ajuste manual de bonos"
        size="sm"
      >
        {ajusteModal && (
          <div className="space-y-4">
            <p className="text-text-secondary text-sm">
              Alumno: <strong className="text-white">{ajusteModal.profile?.full_name}</strong>
            </p>
            <p className="text-text-secondary text-sm">
              Pack: <strong className="text-white">{ajusteModal.pack?.name}</strong>
            </p>
            <p className="text-text-secondary text-sm">
              Sesiones restantes actuales:{' '}
              <strong className="text-gold-primary text-lg">
                {ajusteModal.sessions_remaining}
              </strong>
            </p>
            <div>
              <label className="label">Ajuste (+ para añadir, - para quitar)</label>
              <input
                type="number"
                className="input"
                value={ajusteVal}
                onChange={(e) => setAjusteVal(+e.target.value)}
              />
              <p className="text-text-secondary text-xs mt-1">
                Resultado: {Math.max(0, ajusteModal.sessions_remaining + ajusteVal)} sesiones
              </p>
            </div>
            <div>
              <label className="label">Motivo (obligatorio)</label>
              <input
                type="text"
                className="input"
                placeholder="Por qué se ajusta este saldo"
                value={ajusteMotivo}
                onChange={(e) => setAjusteMotivo(e.target.value)}
                data-testid="bonos-ajuste-motivo"
              />
              <p className="text-text-secondary text-xs mt-1">
                Queda registrado en la auditoría junto con el ajuste.
              </p>
            </div>
            {ajusteError && (
              <p className="text-error text-sm" data-testid="bonos-ajuste-error">
                {ajusteError}
              </p>
            )}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setAjusteModal(null);
                  setAjusteMotivo('');
                  setAjusteError('');
                }}
                className="btn-ghost"
              >
                Cancelar
              </button>
              <button onClick={ajustarCredito} className="btn-gold">
                Aplicar ajuste
              </button>
            </div>
          </div>
        )}
      </Modal>

      <Confirm
        open={!!confirmToggle}
        onClose={() => setConfirmToggle(null)}
        onConfirm={() => confirmToggle && togglePack(confirmToggle)}
        title={confirmToggle?.active ? 'Desactivar pack' : 'Activar pack'}
        message={`¿${confirmToggle?.active ? 'Desactivar' : 'Activar'} el pack "${confirmToggle?.name}"?`}
        danger={confirmToggle?.active}
      />

      <Confirm
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => confirmDelete && handleDeletePack(confirmDelete)}
        title="Eliminar pack"
        message={`¿Eliminar definitivamente el pack "${confirmDelete?.name}"? Esta acción no se puede deshacer.`}
        danger
      />

      <Modal
        open={!!deleteError}
        onClose={() => setDeleteError(null)}
        title="No se puede eliminar"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-text-secondary text-sm">{deleteError}</p>
          <div className="flex justify-end">
            <button onClick={() => setDeleteError(null)} className="btn-gold">
              Entendido
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
